import SodService from "./sod-service";
import { Column, FetchTableDataParams, GetColumnReponse } from "../types/common/table/table-types";
import {
    ChangeRequest,
    GetRequestStatusesResponse,
    GetRequestDetailedResponse,
    GetRequestResponse,
    CreateRequestForm,
    RequestDetailed,
    GetGroupSystemRoleReponse,
    GroupSystemRole,
    PostRequestModelingRole,
    GetRoleHeaderResponse,
    PreparationForTestingRequestFormState,
    PostPreparationForTestingRequestForm,
} from "@/types/brm";
import { nanoid } from "@reduxjs/toolkit";
import { parseRolePermissionsTree, parseRoleMenu, parseRoleOrgLevels } from "./utils";
import { reportsModelingService } from "./reports-modeling-service";
import { requestStatuses } from "../utils/brm-utils";
import intl from "../i18n/i18n";

class RequestsService extends SodService {
    async getRequests(params: FetchTableDataParams): Promise<{ rows: ChangeRequest[]; total: number }> {
        const config = this.generateConfigForGetEntities(params);

        const data = await this.get("brm/change-request-header", config);
        // const total = data.total;
        // const rows = data && data.rows.map((item:GetRequestResponse) => this.parseRequest(item));
        const rows = data && data[0].map((item: GetRequestResponse) => this.parseRequest(item));
        const total = data[1];

        return {
            rows,
            total,
        };
    }

    async getRequestsFilters(params) {
        return this.getFilters("/brm/change-request-header/filters", params);
    }
    async getEmployeeRequests(params: FetchTableDataParams): Promise<{ rows: ChangeRequest[]; total: number }> {
        const config = this.generateConfigForGetEntities(params);

        const data = await this.get("brm/employee-change-request-headers", config);
        const rows = data && data[0].map((item: GetRequestResponse) => this.parseRequest(item));
        const total = data[1];

        return {
            rows,
            total,
        };
    }

    async createNewRequest(requestData: CreateRequestForm) {
        const data = await this.post("brm/change-request-header", this.parseNewRequestData(requestData));
        return data;
    }

    async saveRequestRoles(requestData: PostRequestModelingRole[], requestId: number) {
        const data = await this.post(`brm/change-request-header/save-roles/${requestId}`, requestData);
        return data;
    }

    async getRequestsColumns(): Promise<Column[]> {
        // const columns = await this.getColumns("/brm/requests/columns");
        const columns: GetColumnReponse[] = [
            {
                columnId: "requestId",
                title: intl.formatMessage({ id: "requests-list-page.requests-table-column.request-id" }),
                type: "string",
                sortable: true,
                filterable: true,
            },
            {
                columnId: "url",
                title: intl.formatMessage({ id: "requests-list-page.requests-table-column.url" }),
                type: "string",
                sortable: false,
                filterable: false,
            },
            {
                columnId: "taskId",
                title: intl.formatMessage({ id: "requests-list-page.requests-table-column.task-id" }),
                type: "string",
                sortable: true,
                filterable: true,
            },
            {
                columnId: "currentStage",
                title: intl.formatMessage({ id: "requests-list-page.requests-table-column.current-stage" }),
                type: "string",
                sortable: true,
                filterable: true,
            },
            {
                columnId: "hasRisks",
                title: intl.formatMessage({ id: "requests-list-page.requests-table-column.has-risk" }),
                type: "string",
                sortable: true,
                filterable: true,
            },
            {
                columnId: "createdByText",
                title: intl.formatMessage({ id: "requests-list-page.requests-table-column.created-by" }),
                type: "string",
                sortable: true,
                filterable: true,
            },
            {
                columnId: "createdAt",
                title: intl.formatMessage({ id: "requests-list-page.requests-table-column.created-at" }),
                type: "datetime",
                sortable: true,
                filterable: true,
            },
            {
                columnId: "updatedByText",
                title: intl.formatMessage({ id: "requests-list-page.requests-table-column.updated-by" }),
                type: "string",
                sortable: true,
                filterable: true,
            },
            {
                columnId: "updatedAt",
                title: intl.formatMessage({ id: "requests-list-page.requests-table-column.updated-at" }),
                type: "datetime",
                sortable: true,
                filterable: true,
            },
            {
                columnId: "requestName",
                title: intl.formatMessage({ id: "requests-list-page.requests-table-column.request-name" }),
                type: "string",
                sortable: true,
                filterable: true,
            },
            // {
            //     columnId: "priority",
            //     title: "Приоритет",
            //     type: "string",
            //     sortable: true,
            //     filterable: true,
            // },
            // {
            //     columnId: "status",
            //     title: "Статус",
            //     type: "string",
            //     sortable: true,
            //     filterable: true,
            // },
        ];
        return columns.map((column) => this.parseColumn(column));
    }

    async getRequestsStatuses(): Promise<GetRequestStatusesResponse> {
        //TODO: Написать запрос
        const data: GetRequestStatusesResponse = {
            completed: 5,
            rejected: 8,
            approved: 10,
            inProgress: 12,
            total: 35,
        };
        return data;
    }

    async getDetaliedRequestRole(requestId: string | number, groupId: string, roleName: string, added: boolean) {
        const roleNameParsed = encodeURIComponent(roleName);

        const data = await this.get(`brm/change-request-header/detailed-role/${requestId}/${groupId}/${roleNameParsed}?${added ? "is_original=true" : ""}`);

        const tree = parseRolePermissionsTree(data.tree);
        const orgLevels = parseRoleOrgLevels(data.org_levels);
        const menu = parseRoleMenu(data.menu);
        const childrens = this.parseChildrenRole(data.children);
        const profile = data.profile || "";

        return { tree, orgLevels, menu, childrens, profile };
    }

    parseChildrenRole(childrens) {
        return childrens.map((item) => ({
            id: nanoid(),
            // id: item.role,
            role: item.role,
            groupId: item.group_id,
            description: item.description,
        }));
    }

    async getRequestDetailed(requestId: string | number): Promise<RequestDetailed> {
        const data = await this.get(`brm/change-request-header/detailed/${requestId}`);
        const requestData = this.parseRequestDetailed(data);
        return requestData;
    }

    async getRequestStages() {
        const data = await this.get("brm/change-request-workflow-meta");
        const stages = data.map((item) => {
            return {
                stage: item.status_id,
                text: item.description,
            };
        });
        return stages;
    }

    async getExistedRoles(params: FetchTableDataParams, signal: AbortSignal, systemId: string, groupId: string): Promise<{ rows: GroupSystemRole[]; total: number }> {
        const config = this.generateConfigForGetEntities(params, signal);
        const data = await this.get(`brm/integration-roles/${groupId}`, config);
        const rows = data && data.rows.map((item: GetGroupSystemRoleReponse) => this.parseSystemGroupRole(item, groupId));
        const total = data.total;
        return {
            rows,
            total,
        };
    }

    async saveRequestComment(requestCommentData: { author: string; comment: string }, requestId: number) {
        await this.post(`brm/change-request-comment/${requestId}`, requestCommentData);
    }

    async moveToRejectStep(requestId: string, actionComment: string) {
        const data = { comment: actionComment };
        await this.post(`brm/reject-step/${requestId}`, data);
    }
    async moveToApproveStep(requestId: string, actionComment: string, riskCheck: boolean) {
        const data = { comment: actionComment };
        return await this.post(`brm/approve-step/${requestId}`, data, { params: { check_sod_conflicts: riskCheck } });
    }

    async setRequestStage(requestId: number, stage: string) {
        await this.post(`brm/manual-move-step/${requestId}`, { new_state: stage });
    }

    async getPfcgTreeForTcodes(groupId: string, tcodesList: string[]) {
        const params = {
            tcodes: tcodesList.join(","),
            group_id: groupId,
        };
        const rows = await this.get(`brm/tcode-permissions-tree`, {
            params,
        });

        return reportsModelingService.parsePermissionsTree(rows, new Set());
    }

    async getPfcgTreeForPermissions(groupId: string, permissionList: string[]) {
        const params = {
            permissions: permissionList.join(","),
            group_id: groupId,
        };
        const rows = await this.get(`brm/tcode-permissions-tree`, {
            params,
        });

        return reportsModelingService.parsePermissionsTree(rows, new Set());
    }

    async getTargetSystemId(groupId: string): Promise<string> {
        const data = await this.get(`brm/change-request-default-system/${groupId}`);

        return data;
    }

    async getRoleHeader(roleName: string, systemId: string): Promise<GetRoleHeaderResponse | null> {
        const config = this.generateConfigForGetEntities({
            role: roleName,
            system_id: systemId
        }, null);
        
        try {
            const role = await this.get("/core/integration/role/detailed", config);
            return role || null;
        } catch {
            return null;
        }
    }

    async getTcodePermissions(groupId: string, tcodesList: string[]) {
        const params = {
            tcodes: tcodesList.join(","),
            group_id: groupId,
        };
        const { rows } = await this.get(`core/integration/tcode-permissions`, {
            params,
        });

        return rows.map((row) => ({
            systemId: row.system_id,
            permission: row.permission,
            transaction: row.transaction,
        }));
    }

    async saveTestingData(form: PreparationForTestingRequestFormState, requestId: string, riskCheck, approveWithConflictsMessage: string) {
        const parsedTestingData = this.parseTestingData(form, requestId);
        if (approveWithConflictsMessage.length > 0) parsedTestingData.comment = approveWithConflictsMessage;
        const data = await this.post(`brm/approve-step/${requestId}`, parsedTestingData, { params: { check_sod_conflicts: riskCheck } });
        return data;
    }

    async calcRequestReport(requestId: string) {
        const response = this.post(`brm/sod-conflict-report/${requestId}`);
        return response;
    }

    async fetchRequestReportResult(requestId: number, params) {
        const config = this.generateConfigForGetEntities(params);
        const data = await this.get(`brm/sod-conflict-report/${requestId}`, config);
        const rows = data && data[0];
        const total = data[1];

        return {
            rows,
            total,
        };
    }

    async getBrmReportFilters(params, requestId) {
        return this.getFilters(`brm/sod-conflict-report/filters/${requestId}`, params);
    }

    async fetchRequestReportResultColumns(reportLevel, reportType) {
        const columns = reportsModelingService.getRoleLevelModelingColumns(reportLevel, reportType);
        return columns;
    }

    async getTasks(params: FetchTableDataParams): Promise<{ rows: []; total: number }> {
        const config = this.generateConfigForGetEntities(params);

        const data = await this.get("brm/employee-change-request-headers", config);
        // const total = data.total;
        // const rows = data && data.rows.map((item:GetRequestResponse) => this.parseRequest(item));
        // const rows = data && data[0].map((item: GetRequestResponse) => this.parseRequest(item));
        const rows = data && data[0];
        const total = data[1];

        return {
            rows,
            total,
        };
    }

    async getTasksFilters(params) {
        return this.getFilters("/brm/employee-change-request-headers/filters", params);
    }

    //@ts-expect-error fix column
    private parseColumn(column: GetColumnReponse): Column {
        return {
            id: column.columnId,
            title: column.title,
            type: column.type,
            sortable: column.sortable,
            filterable: column.filterable,
        };
    }

    private parseRequest(request: GetRequestResponse): ChangeRequest {
        return {
            requestId: request.request_id,
            priority: request.priority,
            url: request.outer_link,
            currentStage: request.status,
            createdBy: request.created_by,
            createdByText: request.author_fullname?.trim() || request.created_by,
            updatedBy: request.updated_by,
            updatedByText: request.updated_by_fullname?.trim() || request.updated_by,
            createdAt: request.created_at,
            updatedAt: request.updated_at,
            hasRisks: request.has_risks,
            status: request.status,
            requestName: request.request_name,
        };
    }
    private parseRequestDetailed(request: GetRequestDetailedResponse): RequestDetailed {
        return {
            requestId: request.request_id,
            priority: request.priority,
            url: request.outer_link,
            createdAt: request.created_at,
            createdBy: request.created_by,
            createdByText: request.author_fullname?.trim() || request.created_by,
            updatedBy: request.updated_by,
            updatedByText: request.updated_by_fullname?.trim() || request.updated_by,
            updatedAt: request.updated_at,
            hasRisks: request.has_risks,
            currentStage: request.status,
            status: requestStatuses[request.status],
            requestSource: request.source_type,
            reportReady: false,
            readMode: request.read_mode,
            currentRequestEditable: true,
            requestName: request.request_name,
            history: request.history.map((item) => {
                return {
                    id: nanoid(),
                    createdAt: item.created_at,
                    newValue: item.new_value,
                    oldValue: item.old_value,
                    operation: item.operation,
                    author: item.author,
                    authorText: item.author_fullname?.trim() || item.author,
                    additionalInfo: item.additional_info,
                    status: item.status,
                };
            }),
            comments: request.comments.map((comment) => {
                return {
                    commentId: comment.comment_id,
                    createdAt: comment.created_at,
                    author: comment.author,
                    authorText: comment.author_fullname?.trim() || comment.author,
                    comment: comment.comment,
                };
            }),
            roles: request.roles.map((role) => {
                return {
                    id: role.role,
                    role: role.role,
                    groupId: role.group_id,
                    requestId: role.request_id,
                    description: role.description,
                    // roleType: role.is_complex ? "Групповая" : "Одиночная",
                    version: role.version,
                    isComplex: role.is_complex,
                    children: this.parseChildrenRole(role?.children) || [],
                    isNew: role.is_new || false,
                    added: false,
                    profile: role.profile,
                    updatedAt: role.updated_at,
                    updatedBy: role.updated_by,
                    updatedByText: role.updated_by_fullname?.trim() || role.updated_by,
                    owner: role.owner
                        ? {
                              username: role.owner.username,
                              fullName: role.owner.fullname || "",
                          }
                        : null,
                    deputyOwner: role.deputy_owner
                        ? {
                              username: role.deputy_owner.username,
                              fullName: role.deputy_owner.fullname || "",
                          }
                        : null,
                };
            }),
        };
    }

    private parseNewRequestData(requestData: CreateRequestForm) {
        return {
            // request_id: requestData.requestId,
            outer_link: requestData.requestUrl,
            // priority: requestData.requestPriority,
            // priority: "low",
            // source_type: requestData.requestSource,
        };
    }

    private parseSystemGroupRole(role: GetGroupSystemRoleReponse, groupSystemId: string): GroupSystemRole {
        return {
            id: role.role,
            role: role.role,
            isComplex: role.is_complex_role,
            description: role.description,
            groupId: groupSystemId,
            owner: {
                username: role.owner && role.owner.username,
                fullName: role.owner && role.owner.fullname,
            },
            deputyOwner: {
                username: role.deputy_owner && role.deputy_owner.username,
                fullName: role.deputy_owner && role.deputy_owner.fullname,
            },
        };
    }
    private parseTestingData(form: PreparationForTestingRequestFormState, requestId: string): PostPreparationForTestingRequestForm {
        return {
            request_id: parseInt(requestId),
            // testing_duration: form.testingDuration,
            testing_duration: parseInt(form.testingDuration) || 0,
            testing_user_count: parseInt(form.testingUserCount) || 0,
            testing_responsible: form.testingResponsible.map((item) => item.key),
            additional_roles: form.additionalRoles,
        };
    }
}

const requestsService = new RequestsService();

export { requestsService };
export default RequestsService;
